import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { disconnectEcho } from '../lib/echo';

const STORAGE_KEY = 'pwa_deliverer_token';
const DELIVERER_ID_KEY = 'pwa_deliverer_id';

async function api<T>(method: string, url: string, body?: unknown, token?: string): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error(err.message ?? 'Request failed'), { status: res.status, data: err });
    }
    return res.json() as Promise<T>;
}

export const useDelivererAuthStore = defineStore('deliverer-auth', () => {
    const token = ref<string | null>(localStorage.getItem(STORAGE_KEY));
    const delivererId = ref<string | null>(localStorage.getItem(DELIVERER_ID_KEY));
    const user = ref<{ id: string; name: string; email: string; phone?: string } | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const otpSent = ref(false);
    const otpChannel = ref<'whatsapp' | 'email' | null>(null);
    const otpMessage = ref<string | null>(null);

    const isAuthenticated = computed(() => !!token.value);

    async function sendOtp(phone: string): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const res = await api<{ message: string; channel?: 'whatsapp' | 'email' }>(
                'POST', '/api/v1/auth/deliverer/otp/send', { phone },
            );
            // Resposta anti-enumeração (telefone não cadastrado) não informa `channel` —
            // não assume WhatsApp por padrão, senão mente sobre o canal quando o tenant
            // tem WhatsApp desligado.
            otpChannel.value = res.channel ?? null;
            otpMessage.value = res.message;
            otpSent.value = true;
        } catch (e: unknown) {
            error.value = (e as { data?: { message?: string } })?.data?.message ?? 'Erro ao enviar código.';
        } finally {
            loading.value = false;
        }
    }

    async function verifyOtp(phone: string, code: string): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const res = await api<{ token: string; deliverer_id?: string }>('POST', '/api/v1/auth/deliverer/otp/verify', { phone, code });
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            delivererId.value = res.deliverer_id ?? null;
            if (res.deliverer_id) localStorage.setItem(DELIVERER_ID_KEY, res.deliverer_id);

        } catch {
            error.value = 'Código inválido ou expirado.';
            throw error;
        } finally {
            loading.value = false;
        }
    }

    async function loginWithEmail(email: string, password: string): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const res = await api<{ token: string; deliverer_id?: string | null }>('POST', '/api/v1/auth/deliverer/login', { email, password });
            token.value = res.token;
            localStorage.setItem(STORAGE_KEY, res.token);
            delivererId.value = res.deliverer_id ?? null;
            if (res.deliverer_id) localStorage.setItem(DELIVERER_ID_KEY, res.deliverer_id);

        } catch (e: unknown) {
            error.value = (e as { data?: { message?: string } })?.data?.message ?? 'Credenciais inválidas.';
            throw e;
        } finally {
            loading.value = false;
        }
    }

    function setToken(raw: string): void {
        token.value = raw;
        localStorage.setItem(STORAGE_KEY, raw);
    }

    function logout(): void {
        token.value = null;
        delivererId.value = null;
        user.value = null;
        otpSent.value = false;
        error.value = null;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(DELIVERER_ID_KEY);
        disconnectEcho();
    }

    return { token, delivererId, user, loading, error, otpSent, otpChannel, otpMessage, isAuthenticated, sendOtp, verifyOtp, loginWithEmail, setToken, logout };
});
